import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, first, map } from 'rxjs/operators';
import { environment } from '@app/../environments/environment';
import { State, select$ } from '@app/shared/helpers/store';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Workbook } from 'exceljs';
import * as fs from 'file-saver';
import * as moment from 'moment';
import { Store } from '@ngrx/store';
import { selectCountsSortTypeState, selectCountsState, selectCurrentPage } from '@app/modules/counts/store/selectors/counts.selectors';
import { onMoment } from '@app/shared/helpers/moment';
import { LimitAccessService } from '@app/core/services/limit-access.service';
import { TranslateService } from '@ngx-translate/core';
import { SanitizeStringPipe } from '@app/shared/pipes/sanitize-string.pipe';

@Injectable({
  providedIn: 'root'
})
export class InventoryService {
  public inventoryData$: State<any>;
  public inventory$: Observable<any>;
  public currentIDData$: State<any>;
  public currentID$: Observable<any>;
  public countData$: State<any>;
  public count$: Observable<any>;
  public pageSizeData$: State<any>;
  public pageSize$: Observable<number>;
  public reportTypeData$: State<string>;
  public reportType$: Observable<any>;
  public invoiceData$: State<any>;
  public invoice$: Observable<any>;
  public endpoint: string = environment.apiUrl + '/inventory';
  public reportsEndpoint: string = environment.apiUrl + '/v1/reports';
  public mailEndpoint: string = environment.apiUrl + '/v1/mail';
  public invID: number;
  public invHeaders = ["Count Name", "Count Date", "SKU", "Product Description", "Barcode", "Brand", "UOM", "MSL1", "MSL2", "Category", "Cost", "Retail", "Count Type",
    "Count Type Quantity", "Custom Product Field", "Custom Product Field2", "Count", "Total Cost", "Total Retail", "Total Avail"];
  public currentPageData$: State<number>;
  public currentPage$: Observable<number>;
  public currentPage;
  public total$: Observable<any>;
  public isBasic: boolean = false;
  public mls1Option: boolean = false;
  public mls2Option: boolean = false;

  constructor(private http: HttpClient, private _matSnackbar: MatSnackBar, private store$: Store, private limitAccessService: LimitAccessService, private _translation: TranslateService) {
    this.inventoryData$ = new State({});
    this.inventory$ = select$(this.inventoryData$, data => data);
    this.countData$ = new State({});
    this.count$ = select$(this.countData$, data => data);
    this.currentIDData$ = new State([]);
    this.currentID$ = select$(this.currentIDData$, data => data);
    this.invoiceData$ = new State({});
    this.invoice$ = select$(this.invoiceData$, data => data);
    this.pageSizeData$ = new State(100);
    this.pageSize$ = select$(this.pageSizeData$, data => data);
    this.limitAccessService.arraySettingAccess$.subscribe((arraySettingAccess) => {
      if (arraySettingAccess) {
        let resultIsBasic = this.limitAccessService.getAccess('counts', 'basic', '','');
        let resultMls1 = this.limitAccessService.getAccess('administration', 'settings', 'catalog_settings','mls1');
        let resultMls2 = this.limitAccessService.getAccess('administration', 'settings', 'catalog_settings','mls2');
        this.isBasic = resultIsBasic;
        this.mls1Option = resultMls1;
        this.mls2Option = resultMls2;
      }
    });
  }

  public onGetInventory(active, direction, locations, currentPage?) {
    this.store$.select(selectCountsState).pipe(first()).subscribe(({ currentPage }) => this.currentPage = currentPage);
    const locationsParam = Array.isArray(locations) ? locations.join(',') : locations;
    return this.http.get<any>(`${this.endpoint}?page=${this.currentPage}`, {
      params: {
        pageSize: this.pageSizeData$.getValue(),
        active: active,
        direction: direction,
        locations: locationsParam,
        page: this.currentPage
      }

    });
  }

  setPageSize(size) {
    this.pageSizeData$.next(size);
  }

  public getInventoryItem(invitmID): Observable<any> {
    return this.http.get<any>(`${this.endpoint}/inventory-item/${invitmID}`);
  }

  public getFinalizedInventory(active, direction, currentPage?, recordsByPage?, locations?) {
    const locationsParam = Array.isArray(locations) ? locations.join(',') : locations;
    if (currentPage !== undefined && recordsByPage !== undefined) {
      currentPage = 0;
      recordsByPage = 100000;
    }

    const params = new HttpParams({
      fromString: `active=${active}&direction=${direction}&pageSize=${recordsByPage}&page=${currentPage}&locations=${locationsParam}`
    });
    return this.http.get<any>(`${this.endpoint}/finalized`, {
      params
    }).pipe()
  }

  public setCurrentID(id) {
    this.currentIDData$.next(id);
  }

  public getByDate(start, end) {
    return this.http.get(`${this.endpoint}/date`, {
      params: {
        start,
        end
      }
    }).subscribe(res => {
      this.inventoryData$.next(res)
    });
  }

  public setCurrentType(type) {
    this.reportTypeData$.next(type);
  }

  public onGetCount(id, active, direction): Observable<any> {
    const params = new HttpParams({
      fromString: `active=${active}&direction=${direction}`
    });

    if (this.isFinalizeScreen() == true) {
      return this.http.get(`${this.endpoint}/finalized/count/${id}`, {
        params
      });
    } else {
      return this.http.get(`${this.endpoint}/count/${id}`, {
        params
      });
    }
  }

  public isFinalizeScreen() {

    const is_root = location.pathname == "/";

    if(this.isBasic){
      return false;
    }else{
      if (window.location.href.indexOf("finalized") > -1 || is_root) {
        return true;
      }

      return false;
    }
  }

  public onGetFinancial(id, active, direction) {
    const params = new HttpParams({
      fromString: `active=${active}&direction=${direction}`
    });

    if (this.isFinalizeScreen() == true) {
      return this.http.get(`${this.endpoint}/finalized/financial/${id}`, {
        params
      });
    } else {
      return this.http.get(`${this.endpoint}/financial/${id}`, {
        params
      });
    }
  }

  public onGetReorder(id, active, direction, pcmType) {
    const params = new HttpParams({
      fromString: `active=${active}&direction=${direction}&pcmType=${pcmType}`
    });

    switch (this.isFinalizeScreen()) {
      case true:
        return this.http.get(`${this.endpoint}/finalized/reorder/${id}`, {
          params
        });
      case false:
        return this.http.get(`${this.endpoint}/reorder/${id}`, {
          params
        });
    }
  }

  public onGetPOWorkSheet(value, id) {
    switch (this.isFinalizeScreen()) {
      case true:
        return this.http.get(`${this.endpoint}/finalized/po/${id}`, {
          params: { prdMinQty: value }
        });
      case false:
        return this.http.get(`${this.endpoint}/po/${id}`, {
          params: { prdMinQty: value }
        });
    }
  }

  public onGetPSWorkSheet(value, id) {
    switch (this.isFinalizeScreen()) {
      case true:
        return this.http.get(`${this.endpoint}/finalized/ps/${id}`, {
          params: { prdMinQty: value }
        });
      case false:
        return this.http.get(`${this.endpoint}/ps/${id}`, {
          params: { prdMinQty: value }
        });
    }
  }

  public generatePOPDF(prdMinQty, finalized = 0) {
    this.store$.select(selectCountsState).pipe(first()).subscribe(({ currentInventory }) => {
      this.invID = currentInventory.inv.invID
      return this.http.get(`${this.reportsEndpoint}/by-category/${this.invID}/${finalized}`, {
        params: {
          prdMinQtyOption: prdMinQty
        },
        responseType: 'blob'
      }).pipe(first()).subscribe((data: Blob) => {
        let pdf = new Blob([data], { type: 'application/pdf' });
        let url = URL.createObjectURL(pdf);
        window.open(url);
      });
    });
  }

  public generatePSPDF(prdMinQty, finalized = 0) {
    this.store$.select(selectCountsState).pipe(first()).subscribe(({ currentInventory }) => {
      this.invID = currentInventory.inv.invID
      return this.http.get(`${this.reportsEndpoint}/by-productStorage/${this.invID}/${finalized}`, {
        params: {
          prdMinQtyOption: prdMinQty
        },
        responseType: 'blob'
      }).pipe(first()).subscribe((data: Blob) => {
        let pdf = new Blob([data], { type: 'application/pdf' });
        let url = URL.createObjectURL(pdf);
        window.open(url);
      });
    });
  }

  onCreateCountPDF(id, active, direction, finalized = 0) {
    const params = new HttpParams({
      fromString: `active=${active}&direction=${direction}`
    });
    return this.http.get(`${this.reportsEndpoint}/by-count/${id}/${finalized}`, {
      responseType: 'blob',
      params
    }).pipe(first()).subscribe((data: Blob) => {
      let pdf = new Blob([data], { type: 'application/pdf' });
      let url = URL.createObjectURL(pdf);
      window.open(url);
    });
  }


  onSendReportPDF(data) {
    const params = new HttpParams()
    .set('active', data.active || 'prdNameEn')
    .set('direction', data.direction || 'asc')
    .set('id', data.id.toString() || '')
    .set('finalized', data.finalized.toString() || '0')
    .set('report_type', data.type)
    .set('pcm_type', data.pcm_type)
    .set('print_type', data.print_type)
    .set('min_qty_option', data.min_qty_option)
    .set('email', data.email || '');

  return this.http.get(`${this.mailEndpoint}/send-count-report-pdf`, { params })
    .pipe(first())
    .subscribe((response: JSON) => {
    });
  }


  onCreateCountPDFWithParams(id) {
    switch (this.isFinalizeScreen()) {
      case true:
        return this.http.get(`${this.reportsEndpoint}/by-count/${id}/1`, {
          responseType: 'blob'
        }).pipe(first())
          .subscribe((data: Blob) => {
            let pdf = new Blob([data], { type: 'application/pdf' });
            let url = URL.createObjectURL(pdf);
            window.open(url);
          });
      case false:
        return this.http.get(`${this.reportsEndpoint}/by-count/${id}/0`, {
          responseType: 'blob'
        }).pipe(first())
          .subscribe((data: Blob) => {
            let pdf = new Blob([data], { type: 'application/pdf' });
            let url = URL.createObjectURL(pdf);
            window.open(url);
          });
    }



    return this.http.get(`${this.reportsEndpoint}/by-count/${id}/1`, {
      responseType: 'blob'
    }).pipe(first())
      .subscribe((data: Blob) => {
        let pdf = new Blob([data], { type: 'application/pdf' });
        let url = URL.createObjectURL(pdf);
        window.open(url);
      });
  }


  onCreateReorderPDF(moq, active, direction, finalized = 0) {
    const params = new HttpParams({
      fromString: `active=${active}&direction=${direction}&pcmType=${moq}`
    });
    this.store$.select(selectCountsState).pipe(first()).subscribe(({ currentInventory }) => {
      this.invID = currentInventory.inv.invID
      return this.http.get(`${this.reportsEndpoint}/reorder/${this.invID}/${finalized}`, {
        responseType: 'blob',
        params
      }).pipe(first())
        .subscribe((data: Blob) => {
          let pdf = new Blob([data], { type: 'application/pdf' });
          let url = URL.createObjectURL(pdf);
          window.open(url);
        });
    });
  }

  public onGetPrintCount(id, active, direction) {
    const params = new HttpParams({
      fromString: `active=${active}&direction=${direction}`
    });
    switch (this.isFinalizeScreen()) {
      case true:
        return this.http.get(`${this.endpoint}/finalized/print-count/${id}`, {
          params
        });
      case false:
        return this.http.get(`${this.endpoint}/print-count/${id}`, {
          params
        });
    }


  }

  public generateValuePDF(id, active, direction, finalized = 0) {
    const params = new HttpParams({
      fromString: `active=${active}&direction=${direction}`
    });
    return this.http.get(`${this.reportsEndpoint}/by-value/${id}/${finalized}`, {
      responseType: 'blob',
      params
    }).pipe(first())
      .subscribe((data: Blob) => {
        let pdf = new Blob([data], { type: 'application/pdf' });
        let url = URL.createObjectURL(pdf);
        window.open(url);
      });
  }

  public generatePrintCountPDF(type: string, active, direction, finalized = 0) {
    const params = new HttpParams({
      fromString: `active=${active}&direction=${direction}&type=${type}`
    });
    this.store$.select(selectCountsState).pipe(first()).subscribe(({ currentInventory }) => {
      this.invID = currentInventory.inv.invID
      return this.http.get(`${this.reportsEndpoint}/count-sheet/${this.invID}/${finalized}`, {
        responseType: 'blob',
        params
      }).pipe(first()).subscribe((data: Blob) => {
        let pdf = new Blob([data], { type: 'application/pdf' });
        let url = URL.createObjectURL(pdf);
        window.open(url);
      });
    });
  }

  // this exports the single count sheet to excel
  public onGetExcel(id) {

    switch (this.isFinalizeScreen()) {
      case true:
        return this.http.get(`${this.endpoint}/excel/finalized/${id}`);
      case false:
        return this.http.get(`${this.endpoint}/excel/${id}`);
    }

  }

  public onInventoryExcel(headers, inv, data) {
    const date = moment(new Date()).format("DD/MM/YYYY");
    const arr = [];
    for (let d of data) {
      arr.push([
        new SanitizeStringPipe().transform(d.prdCode),
        new SanitizeStringPipe().transform(d.prdNameEn),
        new SanitizeStringPipe().transform(d.barcodeData),
        d.invitmCount?.toFixed(2),
        d.count.pcmDesc + '/' + d.count.pcmItemQty,
        d.count.pcmCost,
        d.count.pcmRetail,
        d.count.pcmCost * d.invitmCount,
        d.count.pcmRetail * d.invitmCount,
        d.count.pcmMinQty1,
        new SanitizeStringPipe().transform(d.prdCatNameEn),
        d.areas.map(item => new SanitizeStringPipe().transform(item.prdAreaNameEn)).join(', ')
      ]);
    }

    // Create workbook and worksheet
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Sharing Data');

    // Add Header Row
    const headerRow = worksheet.addRow(headers);

    // Cell Style : Fill and Border
    headerRow.eachCell((cell, number) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFFF00' },
        bgColor: { argb: 'E1F4E4' }
      };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    arr.forEach(item => {
      const row = worksheet.addRow(item);
      row.getCell(5);
    })

    worksheet.getColumn(1).width = 15;
    worksheet.getColumn(2).width = 15;
    worksheet.getColumn(3).width = 60;
    worksheet.getColumn(4).width = 15;
    worksheet.getColumn(5).width = 15;
    worksheet.getColumn(6).width = 15;
    worksheet.getColumn(7).width = 15;
    worksheet.getColumn(8).width = 25;
    worksheet.getColumn(9).width = 20;
    worksheet.getColumn(10).width = 15;
    worksheet.getColumn(11).width = 15;
    worksheet.getColumn(12).width = 15;
    worksheet.addRow([]);

    workbook.xlsx.writeBuffer().then((data: any) => {
      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      fs.saveAs(blob, `${inv.invBatchName}.xlsx`);
    });
  }

  public onExcel(headers, inv, data) {
    const date = moment(new Date()).format("DD/MM/YYYY");
    const arr = [];

    for (let d of data) {
      arr.push([
        new SanitizeStringPipe().transform(d.prdCode),
        new SanitizeStringPipe().transform(d.prdNameEn),
        d.barcodeData ? new SanitizeStringPipe().transform(d.barcodeData) : 'N/A',
        d.invitmCount?.toFixed(2) ? d.invitmCount?.toFixed(2) : 'N/A',
        d.count.pcmDesc + '/' + d.count.pcmItemQty,
        d.count.pcmCost,
        d.count.pcmRetail,
        d.count.pcmCost * d.invitmCount,
        d.count.pcmRetail * d.invitmCount,
        d.count.pcmMinQty1,
        new SanitizeStringPipe().transform(d.prdCatNameEn),
        d.areas.map(item => new SanitizeStringPipe().transform(item.prdAreaNameEn)).join(', ')
      ]);
    }

    // Create workbook and worksheet
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Sharing Data');

    // Add Header Row
    const headerRow = worksheet.addRow(headers);

    // Cell Style : Fill and Border
    headerRow.eachCell((cell, number) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFFF00' },
        bgColor: { argb: 'E1F4E4' }
      };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    arr.forEach(item => {
      const row = worksheet.addRow(item);
      row.getCell(5);
    })

    worksheet.getColumn(1).width = 15;
    worksheet.getColumn(2).width = 15;
    worksheet.getColumn(3).width = 60;
    worksheet.getColumn(4).width = 15;
    worksheet.getColumn(5).width = 15;
    worksheet.getColumn(6).width = 15;
    worksheet.getColumn(7).width = 15;
    worksheet.getColumn(8).width = 25;
    worksheet.getColumn(9).width = 20;
    worksheet.getColumn(10).width = 15;
    worksheet.getColumn(11).width = 15;
    worksheet.getColumn(12).width = 15;
    worksheet.addRow([]);

    workbook.xlsx.writeBuffer().then((data: any) => {
      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      fs.saveAs(blob, `${inv.invBatchName}.xlsx`);
    });
  }

  public onExcelCount(headers, inv, data) {
    const date = moment(new Date()).format("DD/MM/YYYY");
    const arr = [];

    if (!this.mls1Option) {
      headers = headers.filter(header => header !== "MSL1");
    }

    if (!this.mls2Option) {
      headers = headers.filter(header => header !== "MSL2");
    }


    for (let d of data) {

      const item = [
        new SanitizeStringPipe().transform(d.prdCode),
        new SanitizeStringPipe().transform(d.prdNameEn),
        d.barcodeData ? new SanitizeStringPipe().transform(d.barcodeData) : 'N/A',
        d.prdBrand ? new SanitizeStringPipe().transform(d.prdBrand) : 'N/A',
        d.prdCasePack ? d.prdCasePack : 'N/A',
        d.prdCatNameEn ? new SanitizeStringPipe().transform(d.prdCatNameEn) : 'N/A',
        //d.areas.length > 0 ? new SanitizeStringPipe().transform(d.areas) : 'N/A',
        d.pcmCost ? parseFloat(d.pcmCost) : 'N/A',
        d.pcmRetail ? parseFloat(d.pcmRetail) : 'N/A',
        d.pcmDesc ? d.pcmDesc : 'N/A',
        d.pcmItemQty ? d.pcmItemQty : 'N/A',
        d.prdCustomField1 ? new SanitizeStringPipe().transform(d.prdCustomField1) : 'N/A',
        d.prdCustomField2 ? new SanitizeStringPipe().transform(d.prdCustomField2) : 'N/A',
        d.invitmCount ? parseFloat(d.invitmCount) : 'N/A',
        parseFloat(d.totalCost) ?? parseFloat(d.totalCost),
        parseFloat(d.totalRetail) ?? parseFloat(d.totalRetail),
        parseFloat(d.totalProfit) ?? parseFloat(d.totalProfit)
      ];

      if (this.mls1Option) {
        item.splice(5, 0, d.pcmMinQty1 ? d.pcmMinQty1 : 'N/A');
      }

      if (this.mls2Option) {
        item.splice(6, 0, d.pcmMinQty2 ? d.pcmMinQty2 : 'N/A');
      }

      arr.push(item);
    }

    // Create workbook and worksheet
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Sharing Data');

    // Add Header Row
    const headerRow = worksheet.addRow(headers);

    // Cell Style : Fill and Border
    headerRow.eachCell((cell, number) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'E1F4E4' },
        bgColor: { argb: 'FFFFFF' }
      };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    arr.forEach(item => {
      const row = worksheet.addRow(item);
      row.getCell(5);
    })

    worksheet.getColumn(1).width = 15;
    worksheet.getColumn(2).width = 60;
    worksheet.getColumn(3).width = 50;
    worksheet.getColumn(4).width = 15;
    worksheet.getColumn(5).width = 15;
    worksheet.getColumn(6).width = 15;
    worksheet.getColumn(7).width = 15;
    worksheet.getColumn(8).width = 25;
    worksheet.getColumn(9).width = 20;
    worksheet.getColumn(10).width = 15;
    worksheet.getColumn(11).width = 15;
    worksheet.getColumn(12).width = 30;
    worksheet.getColumn(13).width = 30;
    worksheet.addRow([]);

    workbook.xlsx.writeBuffer().then((data: any) => {
      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      fs.saveAs(blob, `${inv.invBatchName}.xlsx`);
    });
  }

  async onGenerateExcelAll() {
    const data = await this.http.get(`${this.endpoint}/excel`).toPromise();
    // this.onCreateExcel(data['invItems']);
  }

  async onGenerateCSVAll() {
    const data = await this.http.get(`${this.endpoint}/excel`).toPromise();
    this.onCreateCSV(data['invItems']);
  }

  private onCreateCSV(data) {
    const options = {
      fieldSeparator: ',',
      filename: 'Product List',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      showTitle: true,
      title: 'Product-List',
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true
    };

  }

  async onGenerateExcel(headers, id) {
    const data = await this.http.get(`${this.endpoint}/excel/${id}`).toPromise();
    this.onInventoryExcel(headers, data['inv'], data['invItems']);
  }

  public onDelete(id: number) {
    return this.http.delete(`${this.endpoint}/${id}`);
  }

  // Undo a draft-count deletion (un-archive). Used by the activity-log Restore button.
  public restoreCount(id: number) {
    return this.http.post(`${this.endpoint}/${id}/restore`, {});
  }

  public onDeleteProduct(invID: number, prodID: number) {
    return this.http.delete(`${this.endpoint}/product/${invID}/${prodID}`);
  }

  public getMostRecentCounts() {
    return this.http.get(`${this.endpoint}/most-recent`);
  }

  public getMostRecentCountsBasic() {
    return this.http.get(`${this.endpoint}/most-recent-basic`);
  }


  public onSortCategoriesMaster(products) {
    const headers = new HttpHeaders();
    headers.set('Content-type', 'application/json');
    products = products.map(item => item.prdCatID);
    this.http.patch(`${this.endpoint}/master/sort`, {
      products
    }).pipe(first()).subscribe(item => {

    });
  }

  public onSortByCatMasterCategory(categories) {
    const headers = new HttpHeaders();
    headers.set('Content-type', 'application/json');
    this.http.patch(`${this.endpoint}/master/sort`, {
      categories
    }).pipe(first()).subscribe(item => {
      this._matSnackbar.open('Successfully Sorted.', 'x', {
        panelClass: 'success-snackbar',
        duration: 6000
      });
    });
  }

  public onSortByCatMasterProducts(products) {
    const headers = new HttpHeaders();
    headers.set('Content-type', 'application/json');
    products = products.map(item => item.prdID);
    this.http.patch(`${this.endpoint}/master/sort/products`, {
      products
    }).pipe(first()).subscribe(item => {
      this._matSnackbar.open(this._translation.instant('inventory.successfully_sorted'), 'x', {
        panelClass: 'success-snackbar',
        duration: 6000
      });
    });
  }

  onPaginate(page) {
    this.currentPageData$.next(page);
  }

  public onGetInvoice() {
    const id = this.currentIDData$.getValue();
    return this.http.get(`${this.endpoint}/invoice/${id}`)
      .pipe(first())
      .subscribe(data => {
        this.invoiceData$.next(data)
      });
  }

  public onGetInvoicePDF() {
    const id = this.currentIDData$.getValue();
    return this.http.get(`${this.endpoint}/pdf/invoice/${id}`, {
      responseType: 'blob'
    })
      .subscribe((data: Blob) => {
        let pdf = new Blob([data], { type: 'application/pdf' });
        let url = URL.createObjectURL(pdf);
        window.open(url);
      });
  }

  public finalizeCounts(
    inventory: number[],
    overrideOnhand?: boolean,
    overrideMessage?: string,
    overrideItems?: any[]
  ): Observable<any> {
    return this.http.patch(`${this.endpoint}/finalized`, {
      inventory: inventory,
      overrideOnhand: overrideOnhand,
      overrideMessage: overrideMessage,
      overrideItems: overrideItems
    });
  }

  public checkVariance(inventory: number[]): Observable<any> {
    return this.http.post(`${this.endpoint}/finalized/check-variance`, {
      inventory: inventory
    });
  }

  public searchFinalizedCounts(query, currentPage, recordsByPage, locations): Observable<any> {
    const locationsParam = Array.isArray(locations) ? locations.join(',') : locations;
    return this.http.get(`${this.endpoint}/finalized/search`, {
      params: {
        query: query,
        page: currentPage,
        pageSize: recordsByPage,
        locations: locationsParam
      }
    });
  }

  public searchDraftCounts(sort, direction,query, locations): Observable<any> {
    const locationsParam = Array.isArray(locations) ? locations.join(',') : locations;
    return this.http.get(`${this.endpoint}/draft/search`, {
      params: {
        query: query,
        pageSize: this.pageSizeData$.getValue(),
        sortType: sort ? sort : 'invBatchName',
        direction: direction ? direction : 'ASC',
        locations: locationsParam,
        page: this.currentPage
      }
    });
  }

  public draftCounts(inventory: number[]): Observable<any> {
    return this.http.patch(`${this.endpoint}/draft`, {
      inventory: inventory
    });
  }

  public generateMultipleCounts(inventory) {

    const uniqueInventory = [...new Set(inventory)];
    return this.http.post(`${this.endpoint}/multiple`, {
      inventory: uniqueInventory
    })
      .pipe(first())
      .subscribe(({ data }: any) => {
        // Create workbook and worksheet
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Inventory Counts');

        //TODO: Do we need this?
        /*
        if (!this.mls1Option) {
          this.invHeaders = this.invHeaders.filter(header => header !== "MSL1");
        }

        if (!this.mls2Option) {
          this.invHeaders = this.invHeaders.filter(header => header !== "MSL2");
        }
        */

        // Add Header Row
        const headerRow = worksheet.addRow(this.invHeaders);

        // Cell Style : Fill and Border
        headerRow.eachCell((cell, number) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'E1F4E4' },
            bgColor: { argb: 'FFFFFF' }
          };
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        for (let item of data) {
          const modifiedItems = item['invItems'].map(invItem => {
            return [
                item['inv'].invBatchName,
                onMoment(item['inv'].invStartTS),
                ...invItem
            ];
          });
          item['invItems'] = [...modifiedItems];
          worksheet.addRows(modifiedItems);
        }

        worksheet.getColumn(1).width = 60
        worksheet.getColumn(2).width = 25
        worksheet.getColumn(3).width = 15;
        worksheet.getColumn(4).width = 60;
        worksheet.getColumn(5).width = 15;
        worksheet.getColumn(6).width = 15;
        worksheet.getColumn(7).width = 15;
        worksheet.getColumn(8).width = 15;
        worksheet.getColumn(9).width = 15;
        worksheet.getColumn(10).width = 25;
        worksheet.getColumn(11).width = 20;
        worksheet.getColumn(12).width = 15;
        worksheet.getColumn(13).width = 15;
        worksheet.getColumn(14).width = 30;
        worksheet.getColumn(15).width = 30;
        worksheet.addRow([]);

        workbook.xlsx.writeBuffer().then((data: any) => {
          const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          fs.saveAs(blob, `trakrSuiteCounts.xlsx`);
        });
      });
  }
}
